import { useState } from 'react';
import { Home, Users, Calendar, MessageCircle } from 'lucide-react';
import { ChannelNav } from '@/components/feed/ChannelNav';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useUnreadMessageCount } from '@/hooks/useMessages';

const mainNavItems = [
  { icon: Home, label: 'Home', path: '/home' },
  { icon: Users, label: 'Directory', path: '/directory' },
  { icon: Calendar, label: 'Events', path: '/events' },
  { icon: MessageCircle, label: 'Messages', path: '/messages' },
];

export function DesktopSidebar() {
  const { data: unreadCount } = useUnreadMessageCount();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <aside
      className={cn(
        'hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 bg-card border-r border-border overflow-hidden transition-all duration-200 ease-out',
        isExpanded ? 'lg:w-64 shadow-xl' : 'lg:w-20'
      )}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Logo Header */}
      <div
        className={cn(
          'flex px-4 py-4',
          isExpanded ? 'items-center gap-3 justify-start' : 'items-center justify-center'
        )}
        style={{ background: 'hsl(217 69% 11%)' }}
      >
        <img src="/qbn-logo.png" alt="QBN" className="w-10 h-10 rounded-lg" />
        {isExpanded && (
          <div>
            <h1 className="font-display font-bold text-[13px] leading-tight text-white">Queer Business<br />Network</h1>
          </div>
        )}
      </div>

      {/* Main Navigation */}
      <nav className={cn('flex-1 py-4 space-y-1 overflow-y-auto', isExpanded ? 'px-4' : 'px-2')}>
        {mainNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-lg transition-all font-medium',
                isExpanded ? 'gap-3 px-4 py-3 justify-start' : 'px-3 py-3 justify-center',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )
            }
          >
            <span className="relative">
              <item.icon className="h-5 w-5" />
              {!isExpanded && item.path === '/messages' && unreadCount && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[12px] h-[12px] bg-destructive text-destructive-foreground text-[9px] font-medium rounded-full flex items-center justify-center px-0.5">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </span>
            {isExpanded && <span className="flex-1">{item.label}</span>}
            {isExpanded && item.path === '/messages' && unreadCount && unreadCount > 0 && (
              <span className="min-w-[20px] h-[20px] bg-destructive text-destructive-foreground text-xs font-medium rounded-full flex items-center justify-center px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </NavLink>
        ))}

        {/* Channel List */}
        <div className="pt-4">
          <ChannelNav compact={!isExpanded} />
        </div>
      </nav>
    </aside>
  );
}
